import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiUsers, FiX, FiCheck, FiClock } = FiIcons;

const BookingWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [bookingData, setBookingData] = useState({
    checkIn: '',
    checkOut: '',
    guests: 2,
    roomType: '',
    addSafari: false
  });

  const roomTypes = [
    { id: 'standard', name: 'Standard Double Room', price: 45, capacity: 2 },
    { id: 'family', name: 'Family Room', price: 65, capacity: 4 },
    { id: 'suite', name: 'Safari Suite', price: 85, capacity: 2 }
  ];

  const calculateNights = () => {
    if (bookingData.checkIn && bookingData.checkOut) {
      const checkIn = new Date(bookingData.checkIn);
      const checkOut = new Date(bookingData.checkOut);
      const timeDiff = checkOut.getTime() - checkIn.getTime();
      return Math.ceil(timeDiff / (1000 * 3600 * 24));
    }
    return 0;
  };

  const calculateTotal = () => {
    const selectedRoom = roomTypes.find(room => room.id === bookingData.roomType);
    if (!selectedRoom) return 0;
    
    const nights = calculateNights();
    const roomTotal = selectedRoom.price * nights;
    const safariCost = bookingData.addSafari ? 35 * bookingData.guests : 0;
    return roomTotal + safariCost;
  };

  const handleInputChange = (field, value) => {
    setBookingData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const nextStep = () => {
    if (step < 3) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = () => {
    // Here you would integrate with your booking system
    alert('Booking request submitted! We will contact you within 2 hours to confirm your reservation.');
    setIsOpen(false);
    setStep(1);
  };

  return (
    <>
      {/* Floating Book Now Button */}
      <motion.button
        className="fixed bottom-6 right-6 z-40 bg-gradient-to-r from-orange-600 to-red-600 text-white px-6 py-4 rounded-full font-semibold shadow-lg pulse-glow"
        onClick={() => setIsOpen(true)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        animate={{ 
          y: [0, -5, 0],
        }}
        transition={{ 
          y: { duration: 2, repeat: Infinity, ease: "easeInOut" }
        }}
      >
        Book Now
      </motion.button>

      {/* Booking Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              className="glass-effect rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h3 className="text-3xl font-playfair font-bold text-gray-900">
                    Book Your Stay
                  </h3>
                  <p className="text-gray-600 font-inter">Step {step} of 3</p>
                </div>
                <motion.button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-500 hover:text-gray-700 p-2"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </motion.button>
              </div>

              {/* Progress Bar */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  {[1, 2, 3].map((stepNumber) => (
                    <div
                      key={stepNumber}
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                        stepNumber <= step
                          ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {stepNumber < step ? <SafeIcon icon={FiCheck} className="w-4 h-4" /> : stepNumber}
                    </div>
                  ))}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <motion.div
                    className="bg-gradient-to-r from-orange-600 to-red-600 h-2 rounded-full"
                    initial={{ width: '33%' }}
                    animate={{ width: `${(step / 3) * 100}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>

              {/* Step Content */}
              <AnimatePresence mode="wait">
                {step === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    className="space-y-6"
                  >
                    <h4 className="text-xl font-playfair font-bold text-gray-900 mb-4">
                      Select Your Dates
                    </h4>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-gray-700 font-semibold mb-2 font-inter">
                          Check-in Date
                        </label>
                        <div className="relative">
                          <SafeIcon icon={FiCalendar} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="date"
                            value={bookingData.checkIn}
                            onChange={(e) => handleInputChange('checkIn', e.target.value)}
                            className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all font-inter"
                            min={new Date().toISOString().split('T')[0]}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-semibold mb-2 font-inter">
                          Check-out Date
                        </label>
                        <div className="relative">
                          <SafeIcon icon={FiCalendar} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="date"
                            value={bookingData.checkOut}
                            onChange={(e) => handleInputChange('checkOut', e.target.value)}
                            className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all font-inter"
                            min={bookingData.checkIn || new Date().toISOString().split('T')[0]}
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-700 font-semibold mb-2 font-inter">
                        Number of Guests
                      </label>
                      <div className="relative">
                        <SafeIcon icon={FiUsers} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <select
                          value={bookingData.guests}
                          onChange={(e) => handleInputChange('guests', parseInt(e.target.value))}
                          className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all font-inter"
                        >
                          <option value={1}>1 Guest</option>
                          <option value={2}>2 Guests</option>
                          <option value={3}>3 Guests</option>
                          <option value={4}>4 Guests</option>
                        </select>
                      </div>
                    </div>

                    {calculateNights() > 0 && (
                      <div className="bg-orange-50 rounded-xl p-4">
                        <p className="text-orange-700 font-semibold font-inter">
                          {calculateNights()} night{calculateNights() > 1 ? 's' : ''} selected
                        </p>
                      </div>
                    )}
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    className="space-y-6"
                  >
                    <h4 className="text-xl font-playfair font-bold text-gray-900 mb-4">
                      Choose Your Room
                    </h4>
                    
                    <div className="space-y-4">
                      {roomTypes.map((room) => (
                        <motion.div
                          key={room.id}
                          className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                            bookingData.roomType === room.id
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-gray-200 hover:border-orange-300'
                          }`}
                          onClick={() => handleInputChange('roomType', room.id)}
                          whileHover={{ scale: 1.02 }}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <h5 className="font-semibold text-gray-900 font-inter">{room.name}</h5>
                              <p className="text-gray-600 text-sm">Up to {room.capacity} guests</p>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-orange-600">${room.price}</div>
                              <div className="text-gray-600 text-sm">per night</div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    <div className="border-t pt-6">
                      <h5 className="font-semibold text-gray-900 mb-4 font-inter">Add Safari Experience</h5>
                      <motion.div
                        className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                          bookingData.addSafari
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-orange-300'
                        }`}
                        onClick={() => handleInputChange('addSafari', !bookingData.addSafari)}
                        whileHover={{ scale: 1.02 }}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <h6 className="font-semibold text-gray-900 font-inter">Morning Safari with Niluka Guides</h6>
                            <p className="text-gray-600 text-sm">5:30 AM - 9:30 AM | Expert wildlife guides</p>
                          </div>
                          <div className="text-right">
                            <div className="text-xl font-bold text-orange-600">$35</div>
                            <div className="text-gray-600 text-sm">per person</div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>
                )}

                {step === 3 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    className="space-y-6"
                  >
                    <h4 className="text-xl font-playfair font-bold text-gray-900 mb-4">
                      Booking Summary
                    </h4>
                    
                    <div className="glass-effect rounded-xl p-6">
                      <div className="space-y-4">
                        <div className="flex justify-between">
                          <span className="text-gray-600 font-inter">Dates:</span>
                          <span className="font-semibold">
                            {bookingData.checkIn} to {bookingData.checkOut}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 font-inter">Nights:</span>
                          <span className="font-semibold">{calculateNights()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 font-inter">Guests:</span>
                          <span className="font-semibold">{bookingData.guests}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 font-inter">Room:</span>
                          <span className="font-semibold">
                            {roomTypes.find(r => r.id === bookingData.roomType)?.name}
                          </span>
                        </div>
                        {bookingData.addSafari && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 font-inter">Safari:</span>
                            <span className="font-semibold">Morning Safari ({bookingData.guests} guests)</span>
                          </div>
                        )}
                        
                        <div className="border-t pt-4">
                          <div className="flex justify-between text-xl font-bold">
                            <span>Total:</span>
                            <span className="text-orange-600">${calculateTotal()}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-orange-50 rounded-xl p-4">
                      <div className="flex items-start space-x-3">
                        <SafeIcon icon={FiClock} className="w-5 h-5 text-orange-600 mt-1" />
                        <div>
                          <p className="text-orange-700 font-semibold font-inter">Quick Confirmation</p>
                          <p className="text-orange-600 text-sm font-inter">
                            We'll contact you within 2 hours to confirm availability and finalize your booking.
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8">
                <motion.button
                  onClick={prevStep}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                    step === 1
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  disabled={step === 1}
                  whileHover={step > 1 ? { scale: 1.05 } : {}}
                  whileTap={step > 1 ? { scale: 0.95 } : {}}
                >
                  Previous
                </motion.button>

                <motion.button
                  onClick={step === 3 ? handleSubmit : nextStep}
                  className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-8 py-3 rounded-xl font-semibold ripple-effect"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={
                    (step === 1 && (!bookingData.checkIn || !bookingData.checkOut)) ||
                    (step === 2 && !bookingData.roomType)
                  }
                >
                  {step === 3 ? 'Submit Booking Request' : 'Next'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default BookingWidget;